import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Check if already dismissed this session
        if (sessionStorage.getItem('pwa-install-dismissed')) {
            setDismissed(true);
        }

        const handleBeforeInstall = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);

            // Delay showing to avoid being annoying on first load
            setTimeout(() => {
                if (!sessionStorage.getItem('pwa-install-dismissed')) {
                    setShowPrompt(true);
                }
            }, 5000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        console.log('[PWA] Install outcome:', outcome);
        setDeferredPrompt(null);
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        setDismissed(true);
        sessionStorage.setItem('pwa-install-dismissed', 'true');
    };

    if (!showPrompt || dismissed || !deferredPrompt) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-gradient-to-r from-card to-raised border border-line-strong rounded-xl p-4 shadow-2xl">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-success/10 rounded-lg">
                        <Download className="w-5 h-5 text-success" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-fg text-sm">Install ForgeDash</h3>
                        <p className="text-xs text-fg-3 mt-1">
                            Get quick access from your home screen. Works offline.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                            <button
                                onClick={handleInstall}
                                className="flex-1 py-2 bg-success-fill text-on-fill text-xs font-bold rounded-lg hover:bg-success-fill transition-colors"
                            >
                                Install
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="px-3 py-2 text-fg-3 text-xs hover:text-fg transition-colors"
                            >
                                Not now
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-fg-4 hover:text-fg transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};
